import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  InputAdornment
} from "@material-ui/core";

import {
  AddOutlined as AddOutlinedIcon,
  CloudUploadOutlined as CloudUploadOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Search as SearchIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  PrintOutlined as PrintOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  EditOutlined as EditOutlinedIcon
} from "@material-ui/icons";

import { useAlert } from "react-alert";

import {
  ClientInformationFindAll,
  ClientInformationLocations,
  ClientInformationPrintList,
  ClientInformationCreate
} from "../../adminModel/clientInformation";

const View = () => {
  const alert = useAlert();

  // =========================================================
  // TABLE STATES
  // =========================================================

  const [loading, setLoading] = useState(false);

  const [customers, setCustomers] = useState([]);

  const [locations, setLocations] = useState([]);

  const [page, setPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [total, setTotal] = useState(0);

  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");

  const [search, setSearch] = useState("");

  const [location, setLocation] = useState("");

  // =========================================================
  // DOWNLOAD / PRINT
  // =========================================================

  const [printLoading, setPrintLoading] = useState(false);

  // =========================================================
  // NEW CLIENT MODAL
  // =========================================================

  const [openNewClient, setOpenNewClient] = useState(false);

  const [savingClient, setSavingClient] = useState(false);

  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    contact: "",
    tel: "",
    address: "",
    place: "",
    country: "LOCAL",
    is_active: 1,
    is_top: 0
  });

  // =========================================================
  // LOAD CUSTOMERS
  // =========================================================

  const loadCustomers = async () => {
    setLoading(true);

    try {
      const response = await ClientInformationFindAll({
        page,
        limit: rowsPerPage,
        search,
        place: location
      });

      let rows = [];
      let totalRecords = 0;
      let pages = 1;

      // -------------------------------------------------------
      // DATA
      // -------------------------------------------------------

      if (Array.isArray(response?.data)) {
        rows = response.data;
      } else if (
        Array.isArray(response?.data?.data)
      ) {
        rows = response.data.data;
      }

      // -------------------------------------------------------
      // PAGINATION
      // -------------------------------------------------------

      if (response?.pagination) {
        totalRecords = Number(
          response.pagination.total || 0
        );

        pages = Number(
          response.pagination.totalPages || 1
        );
      }

      if (response?.data?.pagination) {
        totalRecords = Number(
          response.data.pagination.total || 0
        );

        pages = Number(
          response.data.pagination.totalPages || 1
        );
      }

      // -------------------------------------------------------
      // SET STATES
      // -------------------------------------------------------

      setCustomers(rows);

      setTotal(totalRecords);

      setTotalPages(
        Math.max(pages, 1)
      );

    } catch (error) {
      console.error(
        "CUSTOMER LIST ERROR:",
        error
      );

      setCustomers([]);

      setTotal(0);

      setTotalPages(1);

      alert.error(
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        error?.message ||
        "Failed to load customer information."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOAD LOCATIONS
  // =========================================================

  const loadLocations = async () => {
    try {
      const response =
        await ClientInformationLocations();

      let data = [];

      if (Array.isArray(response?.data)) {
        data = response.data;
      } else if (
        Array.isArray(response?.data?.data)
      ) {
        data = response.data.data;
      }

      setLocations(data);
    } catch (error) {
      console.error(
        "LOCATION ERROR:",
        error
      );

      setLocations([]);
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    loadLocations();
  }, []);

  // =========================================================
  // TABLE LOAD
  // =========================================================

  useEffect(() => {
    loadCustomers();
  }, [
    page,
    rowsPerPage,
    search,
    location
  ]);

  // =========================================================
  // SEARCH
  // =========================================================

  const handleSearchChange = (event) => {
    setSearchInput(
      event.target.value
    );
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      setPage(1);

      setSearch(
        searchInput.trim()
      );
    }
  };

  // =========================================================
  // LOCATION
  // =========================================================

  const handleLocationChange = (event) => {
    setLocation(
      event.target.value
    );

    setPage(1);
  };

  // =========================================================
  // ROWS PER PAGE
  // =========================================================

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(
      Number(
        event.target.value
      )
    );

    setPage(1);
  };

  // =========================================================
  // PAGINATION
  // =========================================================

  const goToPage = (newPage) => {
    if (
      newPage < 1 ||
      newPage > totalPages
    ) {
      return;
    }

    setPage(newPage);
  };

  const pageNumbers = useMemo(() => {
    const pages = [];

    const start = Math.max(
      1,
      page - 2
    );

    const end = Math.min(
      totalPages,
      page + 2
    );

    for (
      let i = start;
      i <= end;
      i++
    ) {
      pages.push(i);
    }

    return pages;
  }, [
    page,
    totalPages
  ]);

  // =========================================================
  // NEW CLIENT
  // =========================================================

  const handleOpenNewClient = () => {
    setClientForm({
      name: "",
      email: "",
      contact: "",
      tel: "",
      address: "",
      place: "",
      country: "LOCAL",
      is_active: 1,
      is_top: 0
    });

    setOpenNewClient(true);
  };

  const handleCloseNewClient = () => {
    if (savingClient) {
      return;
    }

    setOpenNewClient(false);
  };

  const handleClientFormChange = (event) => {
    const {
      name,
      value
    } = event.target;

    setClientForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // =========================================================
  // CREATE NEW CLIENT
  // =========================================================

  const handleCreateClient = async () => {
    if (
      !clientForm.name ||
      !String(
        clientForm.name
      ).trim()
    ) {
      alert.error(
        "Customer name is required."
      );

      return;
    }

    setSavingClient(true);

    try {
      const response =
        await ClientInformationCreate({
          name:
            clientForm.name.trim(),

          email:
            clientForm.email.trim() ||
            null,

          contact:
            clientForm.contact.trim() ||
            null,

          tel:
            clientForm.tel.trim() ||
            null,

          address:
            clientForm.address.trim() ||
            null,

          place:
            clientForm.place.trim() ||
            null,

          country:
            clientForm.country ||
            "LOCAL",

          is_active:
            Number(
              clientForm.is_active
            ),

          is_top:
            Number(
              clientForm.is_top
            )
        });

      // -------------------------------------------------------
      // CHECK RESPONSE
      // -------------------------------------------------------

      if (
        !response ||
        response.S !== true
      ) {
        alert.error(
          response?.errors ||
          response?.message ||
          "Failed to create customer."
        );

        return;
      }

      // -------------------------------------------------------
      // SUCCESS
      // -------------------------------------------------------

      alert.success(
        "Customer created successfully."
      );

      // -------------------------------------------------------
      // CLOSE MODAL
      // -------------------------------------------------------

      setOpenNewClient(false);

      // -------------------------------------------------------
      // RESET FORM
      // -------------------------------------------------------

      setClientForm({
        name: "",
        email: "",
        contact: "",
        tel: "",
        address: "",
        place: "",
        country: "LOCAL",
        is_active: 1,
        is_top: 0
      });

      // -------------------------------------------------------
      // REFRESH LOCATION LIST
      // -------------------------------------------------------

      await loadLocations();

      // -------------------------------------------------------
      // GO TO FIRST PAGE
      // -------------------------------------------------------

      setPage(1);

      // -------------------------------------------------------
      // REFRESH TABLE
      // -------------------------------------------------------

      await loadCustomers();

    } catch (error) {
      console.error(
        "CREATE CLIENT ERROR:",
        error
      );

      alert.error(
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        error?.message ||
        "Failed to create customer."
      );
    } finally {
      setSavingClient(false);
    }
  };

  // =========================================================
  // ESCAPE HTML
  // =========================================================

  const escapeHtml = (value) => {
    return String(
      value || ""
    )
      .replace(
        /&/g,
        "&amp;"
      )
      .replace(
        /</g,
        "&lt;"
      )
      .replace(
        />/g,
        "&gt;"
      )
      .replace(
        /"/g,
        "&quot;"
      )
      .replace(
        /'/g,
        "&#039;"
      );
  };

  // =========================================================
  // DOWNLOAD / PRINT PREVIEW
  // =========================================================

  const handlePrint = async () => {
    setPrintLoading(true);

    try {
      const response =
        await ClientInformationPrintList(
          location
        );

      let rows = [];

      if (
        Array.isArray(
          response?.data
        )
      ) {
        rows = response.data;
      } else if (
        Array.isArray(
          response?.data?.data
        )
      ) {
        rows =
          response.data.data;
      }

      if (!rows.length) {
        alert.show(
          "No customer records available."
        );

        return;
      }

      // -------------------------------------------------------
      // OPEN PREVIEW
      // -------------------------------------------------------

      const printWindow =
        window.open(
          "",
          "_blank"
        );

      if (!printWindow) {
        alert.error(
          "Please allow pop-ups in your browser."
        );

        return;
      }

      const locationTitle =
        location ||
        "All Locations";

      // -------------------------------------------------------
      // GROUP BY 3 COLUMNS
      // -------------------------------------------------------

      const groupedRows = [];

      for (
        let i = 0;
        i < rows.length;
        i += 3
      ) {
        groupedRows.push(
          rows.slice(
            i,
            i + 3
          )
        );
      }

      // -------------------------------------------------------
      // BUILD TABLE ROWS
      // -------------------------------------------------------

      const tableRows =
        groupedRows
          .map((group) => {

            const cells =
              [0, 1, 2]
                .map((index) => {

                  const customer =
                    group[index];

                  if (!customer) {
                    return `
                      <td
                        class="customer-cell empty-cell"
                      >
                        &nbsp;
                      </td>
                    `;
                  }

                  return `
                    <td
                      class="customer-cell"
                    >

                      <div
                        class="customer-name"
                      >
                        ${escapeHtml(
                          customer.name ||
                          "-"
                        )}
                      </div>

                      <div
                        class="customer-address"
                      >
                        ${escapeHtml(
                          customer.address ||
                          "-"
                        )}
                      </div>

                    </td>
                  `;
                })
                .join("");

            return `
              <tr>
                ${cells}
              </tr>
            `;
          })
          .join("");

      // -------------------------------------------------------
      // WRITE PREVIEW PAGE
      // -------------------------------------------------------

      printWindow.document.write(`
        <!DOCTYPE html>

        <html>

        <head>

          <title>
            Customer List
          </title>

          <meta
            charset="UTF-8"
          />

          <style>

            * {
              box-sizing:
                border-box;
            }

            html,
            body {

              margin:
                0;

              padding:
                0;

              background:
                #ffffff;

              font-family:
                Arial,
                Helvetica,
                sans-serif;

              color:
                #111827;

            }

            body {

              padding:
                20px;

            }


            /* ==========================================
               TOOLBAR
            ========================================== */

            .print-toolbar {

              width:
                100%;

              display:
                flex;

              align-items:
                center;

              justify-content:
                space-between;

              padding:
                12px 14px;

              margin-bottom:
                20px;

              border:
                1px solid #dfe3e8;

              border-radius:
                8px;

              background:
                #f8fafc;

            }


            .toolbar-title {

              font-size:
                14px;

              font-weight:
                600;

              color:
                #374151;

            }


            .print-button {

              height:
                40px;

              min-width:
                90px;

              padding:
                0 18px;

              border:
                0;

              border-radius:
                7px;

              background:
                #1976d2;

              color:
                #ffffff;

              font-size:
                14px;

              font-weight:
                600;

              cursor:
                pointer;

            }


            .print-button:hover {

              opacity:
                0.92;

            }


            /* ==========================================
               HEADER
            ========================================== */

            .header {

              margin-bottom:
                20px;

              padding-bottom:
                12px;

              border-bottom:
                1px solid #222222;

            }


            .header h1 {

              margin:
                0;

              font-size:
                28px;

              line-height:
                1.3;

              font-weight:
                700;

              color:
                #111827;

            }


            .header p {

              margin:
                5px 0 0;

              font-size:
                14px;

              color:
                #444444;

            }


            /* ==========================================
               CUSTOMER TABLE
            ========================================== */

            .customer-table {

              width:
                100%;

              border-collapse:
                collapse;

              border-spacing:
                0;

              table-layout:
                fixed;

            }


            /* ==========================================
               CUSTOMER CELL
            ========================================== */

            .customer-cell {

              width:
                33.333333%;

              height:
                125px;

              min-height:
                125px;

              padding:
                16px;

              vertical-align:
                top;

              border:
                1px solid #222222;

              background:
                #ffffff;

              page-break-inside:
                avoid;

              break-inside:
                avoid;

            }


            .empty-cell {

              background:
                #ffffff;

            }


            /* ==========================================
               CUSTOMER NAME
            ========================================== */

            .customer-name {

              font-size:
                18px;

              line-height:
                1.35;

              font-weight:
                700;

              color:
                #111111;

              margin-bottom:
                10px;

              word-break:
                break-word;

            }


            /* ==========================================
               CUSTOMER ADDRESS
            ========================================== */

            .customer-address {

              font-size:
                15px;

              line-height:
                1.55;

              font-weight:
                400;

              color:
                #333333;

              white-space:
                pre-line;

              word-break:
                break-word;

            }


            /* ==========================================
               PRINT MODE
            ========================================== */

            @media print {

              @page {

                margin:
                  8mm;

              }


              html,
              body {

                padding:
                  0;

                margin:
                  0;

              }


              .print-toolbar {

                display:
                  none !important;

              }


              .header {

                margin-bottom:
                  15px;

              }


              .customer-table {

                width:
                  100%;

                border-collapse:
                  collapse;

              }


              .customer-cell {

                border:
                  1px solid #222222;

                height:
                  125px;

                min-height:
                  125px;

                padding:
                  16px;

                page-break-inside:
                  avoid;

              }

            }

          </style>

        </head>


        <body>


          <!-- =========================================
               PRINT TOOLBAR
          ========================================== -->

          <div
            class="print-toolbar"
          >

            <div
              class="toolbar-title"
            >
              Customer List Preview
            </div>


            <button
              class="print-button"
              onclick="window.print()"
            >
              Print
            </button>

          </div>


          <!-- =========================================
               HEADER
          ========================================== -->

          <div
            class="header"
          >

            <h1>
              Customer List
            </h1>

            <p>
              Location:
              ${escapeHtml(
                locationTitle
              )}
            </p>

            <p>
              Total Customers:
              ${rows.length}
            </p>

          </div>


          <!-- =========================================
               CUSTOMER TABLE
          ========================================== -->

          <table
            class="customer-table"
          >

            <tbody>

              ${tableRows}

            </tbody>

          </table>


        </body>

        </html>
      `);

      printWindow.document.close();

      printWindow.focus();

    } catch (error) {
      console.error(
        "PRINT PREVIEW ERROR:",
        error
      );

      alert.error(
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        error?.message ||
        "Failed to prepare customer list."
      );
    } finally {
      setPrintLoading(false);
    }
  };

  // =========================================================
  // STATUS
  // =========================================================

  const getStatus = (
    customer
  ) => {

    return Number(
      customer?.is_active
    ) === 1
      ? "Active"
      : "Inactive";
  };

  // =========================================================
  // COUNTRY
  // =========================================================

  const getCountry = (
    customer
  ) => {

    return (
      customer?.country ||
      "LOCAL"
    );
  };

  // =========================================================
  // ENTRY RANGE
  // =========================================================

  const startEntry =
    total === 0
      ? 0
      : (
          (page - 1) *
          rowsPerPage
        ) + 1;

  const endEntry =
    Math.min(
      page * rowsPerPage,
      total
    );

  // =========================================================
  // BUTTON STYLE
  // =========================================================

  const buttonStyle = {

    height:
      38,

    minHeight:
      38,

    paddingLeft:
      14,

    paddingRight:
      14,

    borderRadius:
      8,

    textTransform:
      "none",

    fontSize:
      13,

    fontWeight:
      600,

    letterSpacing:
      0,

    boxShadow:
      "none"

  };

  // =========================================================
  // ACTION BUTTON STYLE
  // =========================================================

  const actionButtonStyle = {

    minWidth:
      34,

    width:
      34,

    height:
      32,

    padding:
      0,

    borderRadius:
      7,

    borderColor:
      "#d1d5db",

    marginRight:
      4

  };

  // =========================================================
  // RENDER
  // =========================================================

  return (

    <Card

      elevation={0}

      style={{

        borderRadius:
          14,

        border:
          "1px solid #e5e7eb",

        overflow:
          "hidden",

        background:
          "#ffffff"

      }}

    >

      <CardContent
        style={{
          padding:
            0
        }}
      >

        {/* ===================================================
            TOP TOOLBAR
        ==================================================== */}

        <Box

          style={{

            padding:
              "18px 20px",

            borderBottom:
              "1px solid #e5e7eb",

            background:
              "#ffffff"

          }}

        >

          <Box

            display="flex"

            alignItems="center"

            flexWrap="wrap"

            gridGap={10}

          >

            {/* DOWNLOAD TEMPLATE */}

            <Button

              variant="outlined"

              startIcon={
                <DescriptionOutlinedIcon />
              }

              style={{

                ...buttonStyle,

                color:
                  "#374151",

                borderColor:
                  "#d1d5db",

                background:
                  "#ffffff"

              }}

            >

              Download Excel upload template

            </Button>


            {/* NEW CLIENT */}

            <Button

              variant="contained"

              color="primary"

              startIcon={
                <AddOutlinedIcon />
              }

              onClick={
                handleOpenNewClient
              }

              style={{

                ...buttonStyle,

                color:
                  "#ffffff"

              }}

            >

              New Client

            </Button>


            {/* UPLOAD */}

            <Button

              variant="outlined"

              startIcon={
                <CloudUploadOutlinedIcon />
              }

              style={{

                ...buttonStyle,

                color:
                  "#374151",

                borderColor:
                  "#d1d5db",

                background:
                  "#ffffff"

              }}

            >

              Upload File

            </Button>


            {/* DELETE */}

            <Button

              variant="outlined"

              startIcon={
                <DeleteOutlineIcon />
              }

              style={{

                ...buttonStyle,

                color:
                  "#b42318",

                borderColor:
                  "#efb4b4",

                background:
                  "#fffafa"

              }}

            >

              Delete Cluster

            </Button>

          </Box>

        </Box>


        {/* ===================================================
            FILTER BAR
        ==================================================== */}

        <Box

          style={{

            padding:
              "15px 20px",

            background:
              "#fafafa",

            borderBottom:
              "1px solid #e5e7eb"

          }}

        >

          <Box

            display="flex"

            alignItems="center"

            justifyContent="space-between"

            flexWrap="wrap"

            gridGap={12}

          >

            <Box

              display="flex"

              alignItems="center"

              flexWrap="wrap"

              gridGap={10}

            >

              {/* SHOW */}

              <Typography

                variant="body2"

                style={{

                  color:
                    "#6b7280",

                  fontSize:
                    13

                }}

              >

                Show

              </Typography>


              {/* ROWS */}

              <Select

                value={
                  rowsPerPage
                }

                onChange={
                  handleRowsPerPageChange
                }

                variant="outlined"

                style={{

                  width:
                    80,

                  height:
                    38,

                  background:
                    "#ffffff",

                  borderRadius:
                    8,

                  fontSize:
                    13

                }}

              >

                <MenuItem value={10}>
                  10
                </MenuItem>

                <MenuItem value={25}>
                  25
                </MenuItem>

                <MenuItem value={50}>
                  50
                </MenuItem>

                <MenuItem value={100}>
                  100
                </MenuItem>

              </Select>


              <Typography

                variant="body2"

                style={{

                  color:
                    "#6b7280",

                  fontSize:
                    13

                }}

              >

                entries

              </Typography>


              {/* LOCATION */}

              <Select

                value={
                  location
                }

                onChange={
                  handleLocationChange
                }

                displayEmpty

                variant="outlined"

                startAdornment={

                  <InputAdornment
                    position="start"
                  >

                    <LocationOnOutlinedIcon

                      style={{

                        fontSize:
                          18,

                        color:
                          "#9ca3af"

                      }}

                    />

                  </InputAdornment>

                }

                style={{

                  width:
                    200,

                  height:
                    38,

                  background:
                    "#ffffff",

                  borderRadius:
                    8,

                  fontSize:
                    13

                }}

              >

                <MenuItem value="">
                  All Locations
                </MenuItem>


                {locations.map(
                  (
                    item,
                    index
                  ) => {

                    const value =
                      typeof item ===
                      "string"
                        ? item
                        : item.place;

                    return (

                      <MenuItem

                        key={
                          `${value}-${index}`
                        }

                        value={
                          value
                        }

                      >

                        {value}

                      </MenuItem>

                    );

                  }
                )}

              </Select>


              {/* DOWNLOAD */}

              <Button

                variant="outlined"

                startIcon={

                  printLoading

                    ? (
                      <CircularProgress
                        size={16}
                      />
                    )

                    : (
                      <PrintOutlinedIcon />
                    )

                }

                onClick={
                  handlePrint
                }

                disabled={
                  printLoading
                }

                style={{

                  ...buttonStyle,

                  color:
                    "#374151",

                  borderColor:
                    "#d1d5db",

                  background:
                    "#ffffff"

                }}

              >

                {printLoading
                  ? "Preparing..."
                  : "Download"}

              </Button>

            </Box>


            {/* SEARCH */}

            <TextField

              value={
                searchInput
              }

              onChange={
                handleSearchChange
              }

              onKeyDown={
                handleSearchKeyDown
              }

              variant="outlined"

              placeholder="Search customer..."

              size="small"

              style={{

                width:
                  280,

                background:
                  "#ffffff"

              }}

              InputProps={{

                startAdornment:

                  (

                    <InputAdornment
                      position="start"
                    >

                      <SearchIcon

                        style={{

                          fontSize:
                            19,

                          color:
                            "#9ca3af"

                        }}

                      />

                    </InputAdornment>

                  )

              }}

            />

          </Box>

        </Box>


        {/* ===================================================
            TABLE
        ==================================================== */}

        <TableContainer

          style={{

            overflowX:
              "auto"

          }}

        >

          <Table

            style={{

              minWidth:
                1100

            }}

          >

            {/* =================================================
                TABLE HEAD
            ================================================== */}

            <TableHead>

              <TableRow

                style={{

                  background:
                    "#f8fafc"

                }}

              >

                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  ID

                </TableCell>


                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  Name

                </TableCell>


                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  Email

                </TableCell>


                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  Contact

                </TableCell>


                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  Address

                </TableCell>


                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  Country

                </TableCell>


                <TableCell

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px"

                  }}

                >

                  Status

                </TableCell>


                <TableCell

                  align="center"

                  style={{

                    fontWeight:
                      700,

                    color:
                      "#374151",

                    fontSize:
                      13,

                    padding:
                      "14px 18px",

                    width:
                      140

                  }}

                >

                  Action

                </TableCell>

              </TableRow>

            </TableHead>


            {/* =================================================
                TABLE BODY
            ================================================== */}

            <TableBody>

              {loading ? (

                <TableRow>

                  <TableCell

                    colSpan={8}

                    align="center"

                    style={{

                      padding:
                        55

                    }}

                  >

                    <CircularProgress
                      size={28}
                    />


                    <Typography

                      variant="body2"

                      style={{

                        marginTop:
                          12,

                        color:
                          "#6b7280",

                        fontSize:
                          13

                      }}

                    >

                      Loading customer information...

                    </Typography>

                  </TableCell>

                </TableRow>

              ) : customers.length === 0 ? (

                <TableRow>

                  <TableCell

                    colSpan={8}

                    align="center"

                    style={{

                      padding:
                        55

                    }}

                  >

                    <Typography

                      variant="body2"

                      style={{

                        color:
                          "#6b7280",

                        fontSize:
                          13

                      }}

                    >

                      No customer records found.

                    </Typography>

                  </TableCell>

                </TableRow>

              ) : (

                customers.map(
                  (
                    customer
                  ) => (

                    <TableRow

                      key={
                        customer.id
                      }

                      hover

                    >

                      {/* ID */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Typography

                          variant="body2"

                          style={{

                            color:
                              "#6b7280",

                            fontWeight:
                              600,

                            fontSize:
                              13

                          }}

                        >

                          {
                            customer.id
                          }

                        </Typography>

                      </TableCell>


                      {/* NAME */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Typography

                          variant="body2"

                          style={{

                            color:
                              "#111827",

                            fontWeight:
                              600,

                            fontSize:
                              13

                          }}

                        >

                          {
                            customer.name ||
                            "-"
                          }

                        </Typography>

                      </TableCell>


                      {/* EMAIL */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Typography

                          variant="body2"

                          style={{

                            color:
                              "#6b7280",

                            fontSize:
                              13

                          }}

                        >

                          {
                            customer.email ||
                            "-"
                          }

                        </Typography>

                      </TableCell>


                      {/* CONTACT */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Typography

                          variant="body2"

                          style={{

                            color:
                              "#4b5563",

                            fontSize:
                              13

                          }}

                        >

                          {
                            customer.contact ||
                            customer.tel ||
                            "-"
                          }

                        </Typography>

                      </TableCell>


                      {/* ADDRESS */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Typography

                          variant="body2"

                          style={{

                            color:
                              "#4b5563",

                            fontSize:
                              13,

                            maxWidth:
                              300,

                            lineHeight:
                              1.5

                          }}

                        >

                          {
                            customer.address ||
                            "-"
                          }

                        </Typography>

                      </TableCell>


                      {/* COUNTRY */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Chip

                          size="small"

                          label={
                            getCountry(
                              customer
                            )
                          }

                          style={{

                            height:
                              26,

                            borderRadius:
                              6,

                            fontSize:
                              11,

                            fontWeight:
                              600

                          }}

                        />

                      </TableCell>


                      {/* STATUS */}

                      <TableCell

                        style={{

                          padding:
                            "13px 18px"

                        }}

                      >

                        <Chip

                          size="small"

                          label={
                            getStatus(
                              customer
                            )
                          }

                          style={{

                            height:
                              26,

                            borderRadius:
                              6,

                            fontSize:
                              11,

                            fontWeight:
                              600,

                            background:

                              Number(
                                customer.is_active
                              ) === 1

                                ? "#e8f5e9"

                                : "#ffebee",

                            color:

                              Number(
                                customer.is_active
                              ) === 1

                                ? "#2e7d32"

                                : "#c62828"

                          }}

                        />

                      </TableCell>


                      {/* ACTION */}

                      <TableCell

                        align="center"

                        style={{

                          padding:
                            "10px 18px"

                        }}

                      >

                        <Tooltip
                          title="View"
                        >

                          <Button

                            variant="outlined"

                            size="small"

                            style={{
                              ...actionButtonStyle
                            }}

                          >

                            <VisibilityOutlinedIcon

                              style={{
                                fontSize:
                                  17
                              }}

                            />

                          </Button>

                        </Tooltip>


                        <Tooltip
                          title="Edit"
                        >

                          <Button

                            variant="outlined"

                            size="small"

                            style={{
                              ...actionButtonStyle
                            }}

                          >

                            <EditOutlinedIcon

                              style={{
                                fontSize:
                                  17
                              }}

                            />

                          </Button>

                        </Tooltip>


                        <Tooltip
                          title="Delete"
                        >

                          <Button

                            variant="outlined"

                            size="small"

                            style={{

                              ...actionButtonStyle,

                              borderColor:
                                "#efb4b4"

                            }}

                          >

                            <DeleteOutlineIcon

                              style={{

                                fontSize:
                                  17,

                                color:
                                  "#d32f2f"

                              }}

                            />

                          </Button>

                        </Tooltip>

                      </TableCell>

                    </TableRow>

                  )
                )

              )}

            </TableBody>

          </Table>

        </TableContainer>


        {/* ===================================================
            FOOTER + PAGINATION
        ==================================================== */}

        <Box

          display="flex"

          alignItems="center"

          justifyContent="space-between"

          flexWrap="wrap"

          gridGap={12}

          style={{

            padding:
              "15px 20px",

            borderTop:
              "1px solid #e5e7eb",

            background:
              "#ffffff"

          }}

        >

          {/* ENTRY INFO */}

          <Typography

            variant="body2"

            style={{

              color:
                "#6b7280",

              fontSize:
                13

            }}

          >

            Showing{" "}

            {startEntry}

            {" "}to{" "}

            {endEntry}

            {" "}of{" "}

            {total}

            {" "}entries

          </Typography>


          {/* PAGINATION */}

          <Box

            display="flex"

            alignItems="center"

            gridGap={5}

          >

            {/* PREVIOUS */}

            <Button

              size="small"

              variant="outlined"

              disabled={
                page === 1
              }

              onClick={() =>
                goToPage(
                  page - 1
                )
              }

              style={{

                height:
                  34,

                minWidth:
                  80,

                padding:
                  "0 12px",

                borderRadius:
                  7,

                textTransform:
                  "none",

                fontSize:
                  12,

                fontWeight:
                  600,

                borderColor:
                  "#d1d5db"

              }}

            >

              Previous

            </Button>


            {/* PAGE BUTTONS */}

            {pageNumbers.map(
              (
                number
              ) => (

                <Button

                  key={
                    number
                  }

                  size="small"

                  variant={

                    number === page
                      ? "contained"
                      : "outlined"

                  }

                  color={

                    number === page
                      ? "primary"
                      : "default"

                  }

                  onClick={() =>
                    goToPage(
                      number
                    )
                  }

                  style={{

                    minWidth:
                      36,

                    width:
                      36,

                    height:
                      34,

                    padding:
                      0,

                    borderRadius:
                      7,

                    fontSize:
                      12,

                    fontWeight:

                      number === page
                        ? 700
                        : 500,

                    boxShadow:
                      "none"

                  }}

                >

                  {
                    number
                  }

                </Button>

              )
            )}


            {/* NEXT */}

            <Button

              size="small"

              variant="outlined"

              disabled={
                page ===
                totalPages
              }

              onClick={() =>
                goToPage(
                  page + 1
                )
              }

              style={{

                height:
                  34,

                minWidth:
                  65,

                padding:
                  "0 12px",

                borderRadius:
                  7,

                textTransform:
                  "none",

                fontSize:
                  12,

                fontWeight:
                  600,

                borderColor:
                  "#d1d5db"

              }}

            >

              Next

            </Button>

          </Box>

        </Box>


      </CardContent>


      {/* =====================================================
          NEW CLIENT MODAL
      ====================================================== */}

      <Dialog

        open={
          openNewClient
        }

        onClose={
          handleCloseNewClient
        }

        fullWidth

        maxWidth="sm"

        disableBackdropClick={
          savingClient
        }

        disableEscapeKeyDown={
          savingClient
        }

      >

        {/* ===================================================
            DIALOG TITLE
        ==================================================== */}

        <DialogTitle

          style={{

            fontWeight:
              700,

            fontSize:
              20,

            padding:
              "20px 24px 12px"

          }}

        >

          New Client

        </DialogTitle>


        {/* ===================================================
            DIALOG CONTENT
        ==================================================== */}

        <DialogContent

          dividers

          style={{

            padding:
              24

          }}

        >

          <Grid

            container

            spacing={2}

          >


            {/* NAME */}

            <Grid

              item

              xs={12}

            >

              <TextField

                fullWidth

                required

                autoFocus

                label="Name"

                name="name"

                value={
                  clientForm.name
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              />

            </Grid>


            {/* EMAIL */}

            <Grid

              item

              xs={12}

              sm={6}

            >

              <TextField

                fullWidth

                label="Email"

                name="email"

                value={
                  clientForm.email
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              />

            </Grid>


            {/* CONTACT */}

            <Grid

              item

              xs={12}

              sm={6}

            >

              <TextField

                fullWidth

                label="Contact"

                name="contact"

                value={
                  clientForm.contact
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              />

            </Grid>


            {/* TELEPHONE */}

            <Grid

              item

              xs={12}

              sm={6}

            >

              <TextField

                fullWidth

                label="Telephone"

                name="tel"

                value={
                  clientForm.tel
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              />

            </Grid>


            {/* LOCATION */}

            <Grid

              item

              xs={12}

              sm={6}

            >

              <TextField

                fullWidth

                label="Location"

                name="place"

                value={
                  clientForm.place
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              />

            </Grid>


            {/* ADDRESS */}

            <Grid

              item

              xs={12}

            >

              <TextField

                fullWidth

                multiline

                rows={3}

                label="Address"

                name="address"

                value={
                  clientForm.address
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              />

            </Grid>


            {/* COUNTRY */}

            <Grid

              item

              xs={12}

              sm={6}

            >

              <TextField

                select

                fullWidth

                label="Country"

                name="country"

                value={
                  clientForm.country
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              >

                <MenuItem value="LOCAL">

                  LOCAL

                </MenuItem>


                <MenuItem value="INTERNATIONAL">

                  INTERNATIONAL

                </MenuItem>

              </TextField>

            </Grid>


            {/* STATUS */}

            <Grid

              item

              xs={12}

              sm={6}

            >

              <TextField

                select

                fullWidth

                label="Status"

                name="is_active"

                value={
                  clientForm.is_active
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              >

                <MenuItem value={1}>

                  Active

                </MenuItem>


                <MenuItem value={0}>

                  Inactive

                </MenuItem>

              </TextField>

            </Grid>


            {/* TOP CLIENT */}

            <Grid

              item

              xs={12}

            >

              <TextField

                select

                fullWidth

                label="Top Client"

                name="is_top"

                value={
                  clientForm.is_top
                }

                onChange={
                  handleClientFormChange
                }

                variant="outlined"

                size="small"

              >

                <MenuItem value={0}>

                  No

                </MenuItem>


                <MenuItem value={1}>

                  Yes

                </MenuItem>

              </TextField>

            </Grid>


          </Grid>

        </DialogContent>


        {/* ===================================================
            DIALOG ACTIONS
        ==================================================== */}

        <DialogActions

          style={{

            padding:
              "14px 24px"

          }}

        >

          {/* CANCEL */}

          <Button

            onClick={
              handleCloseNewClient
            }

            disabled={
              savingClient
            }

            variant="outlined"

            style={{

              minWidth:
                90,

              height:
                38,

              borderRadius:
                8,

              textTransform:
                "none",

              fontWeight:
                600,

              borderColor:
                "#d1d5db"

            }}

          >

            Cancel

          </Button>


          {/* SAVE */}

          <Button

            onClick={
              handleCreateClient
            }

            disabled={
              savingClient
            }

            variant="contained"

            color="primary"

            style={{

              minWidth:
                120,

              height:
                38,

              borderRadius:
                8,

              textTransform:
                "none",

              fontWeight:
                600,

              boxShadow:
                "none"

            }}

          >

            {savingClient ? (

              <Box

                display="flex"

                alignItems="center"

                gridGap={8}

              >

                <CircularProgress

                  size={18}

                  color="inherit"

                />

                Saving...

              </Box>

            ) : (

              "Save Client"

            )}

          </Button>

        </DialogActions>

      </Dialog>

    </Card>

  );
};

export default View;